import { useState } from 'react'
import { Notice } from '../Toast'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { addConsentText, getConsentTexts, getLogo, removeLogo, uploadLogo } from '../api/endpoints'
import { ApiError } from '../api/client'
import { ErrorNotice } from '../components'
import HowItWorks from '../HowItWorks'
import { formatDateTime } from '../lib/format'
import { DEFAULT_CONSENT } from '../lib/defaultConsent'

export default function SettingsPage() {
  const texts = useQuery({ queryKey: ['consent-texts'], queryFn: getConsentTexts })
  return (
    <div>
      <h1>Settings</h1>
      <HowItWorks id="settings" title="How consent wording works" steps={[
        { icon: 'shield', title: 'Wording from Legal', text: 'Paste the text your Legal team approved. It is what candidates (and, where required, guardians) agree to.' },
        { icon: 'check', title: 'Each version is kept', text: 'A new version becomes the active one. Every consent records the exact words that were shown.' },
        { icon: 'campaigns', title: 'Needed to open a campaign', text: 'A campaign cannot open until candidate wording exists, and guardian wording too when that campaign asks for guardian consent.' },
      ]} />
      <LogoCard />
      <ErrorNotice error={texts.error} />
      {(['CANDIDATE', 'GUARDIAN'] as const).map((audience) => <Audience key={audience} audience={audience} list={(texts.data ?? []).filter((t) => t.audience === audience)} />)}
    </div>
  )
}

function Audience({ audience, list }: { audience: 'CANDIDATE' | 'GUARDIAN'; list: { id: number; version: string; body: string; active: boolean; createdAt: string }[] }) {
  const queryClient = useQueryClient()
  const [version, setVersion] = useState('')
  const [body, setBody] = useState('')
  const active = list.find((t) => t.active)
  const add = useMutation({ mutationFn: () => addConsentText({ audience, version, body }), onSuccess: async () => { setVersion(''); setBody(''); await queryClient.invalidateQueries({ queryKey: ['consent-texts'] }) } })
  const errs = add.error instanceof ApiError ? add.error.fields : {}
  const title = audience === 'CANDIDATE' ? 'Candidate consent' : 'Guardian consent (candidates under 18)'
  return (
    <section className="card" aria-label={title}>
      <h2>{title}</h2>
      {active ? <p className="muted">Active: <b>{active.version}</b>, since {formatDateTime(active.createdAt)}</p> : <Notice tone="info">No wording yet. Add it below.</Notice>}
      {active && <div className="scroll-text" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.55, maxHeight: 240, overflow: 'auto', border: '1px solid var(--line)', borderRadius: 10, padding: 10, marginBottom: 12 }}>{active.body}</div>}
      <form onSubmit={(e) => { e.preventDefault(); add.mutate() }}>
        <p><button type="button" onClick={() => { setVersion(DEFAULT_CONSENT[audience].version); setBody(DEFAULT_CONSENT[audience].body) }}>Fill in the standard wording</button> <span className="muted">A plain-language starting point. Read it, adjust it for your institution, and have your Legal team approve it before you make it active.</span></p>
        <label className="field">New version name<input required value={version} onChange={(e) => setVersion(e.target.value)} placeholder="2027-v1" /></label>{errs.version && <Notice tone="error">{errs.version}</Notice>}
        <label className="field">Wording<textarea required rows={6} value={body} onChange={(e) => setBody(e.target.value)} /></label>{errs.body && <Notice tone="error">{errs.body}</Notice>}
        <ErrorNotice error={Object.keys(errs).length === 0 ? add.error : null} />
        <button type="submit" className="primary" disabled={add.isPending}>{add.isPending ? 'Saving…' : 'Make this the active wording'}</button>
      </form>
      {list.length > 1 && <details style={{ marginTop: 12 }}><summary>Earlier versions ({list.length - 1})</summary><ul>{list.filter((t) => !t.active).map((t) => <li key={t.id}><b>{t.version}</b> · {formatDateTime(t.createdAt)}</li>)}</ul></details>}
    </section>
  )
}

/** The institution's optional logo: shown on the PDF report and on the candidate's results. With none uploaded, nothing is shown. */
function LogoCard() {
  const queryClient = useQueryClient()
  const logo = useQuery({ queryKey: ['logo'], queryFn: getLogo })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['logo'] })
  const upload = useMutation({ mutationFn: (f: File) => uploadLogo(f), onSuccess: refresh })
  const remove = useMutation({ mutationFn: removeLogo, onSuccess: refresh })
  const l = logo.data
  return (
    <section className="card" aria-labelledby="logo-title" style={{ marginBottom: 16 }}>
      <h2 id="logo-title">Institution logo (optional)</h2>
      <p className="muted">Shown at the top of the PDF report and on the results candidates see. PNG or JPEG, under 512 KB; a wide logo about 600 pixels across works well. If you add none, nothing is shown.</p>
      <ErrorNotice error={logo.error ?? upload.error ?? remove.error} />
      {l?.hasLogo && <p><img src={`/api/public/institutions/${l.slug}/logo?v=${encodeURIComponent(l.updatedAt ?? '')}`} alt="Current institution logo" style={{ maxHeight: 72, maxWidth: 280, background: '#fff', padding: 8, borderRadius: 8, border: '1px solid var(--line)' }} /></p>}
      <div className="row">
        <label className="btn">{upload.isPending ? 'Uploading…' : l?.hasLogo ? 'Replace logo' : 'Upload logo'}
          <input type="file" accept="image/png,image/jpeg" hidden disabled={upload.isPending} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) upload.mutate(f, { onSuccess: () => undefined }) }} />
        </label>
        {l?.hasLogo && <button type="button" className="danger" disabled={remove.isPending} onClick={() => remove.mutate()}>Remove logo</button>}
      </div>
      {upload.isSuccess && <Notice tone="ok">Logo saved. New reports will use it.</Notice>}
    </section>
  )
}
